/**
 * Utility functions for authentication and permission handling
 */

/**
 * Extract user ID from storage (localStorage and JWT token)
 * @returns {string} User ID or 'system' if not found
 */
export const getCurrentUserId = () => {
  try {
    // First try to get from localStorage user object
    const userObj = localStorage.getItem('user');
    if (userObj) {
      const user = JSON.parse(userObj);
      if (user && user.id) {
        return user.id;
      }
    }
    
    // If that fails, try to extract from JWT token
    const token = localStorage.getItem('token');
    if (token) {
      // Try to extract user info from JWT
      const parts = token.split('.');
      if (parts.length === 3) {
        const payload = JSON.parse(atob(parts[1]));
        if (payload && payload.id) {
          return payload.id;
        }
      }
    }
    
    // Fallback
    return 'system';
  } catch (error) {
    console.error('Error extracting user ID:', error);
    return 'system';
  }
};

/**
 * Get the current authenticated user object
 * @returns {Object|null} User object or null if not authenticated
 */
export const getCurrentUser = () => {
  try {
    const userJson = localStorage.getItem('user');
    if (!userJson) return null;
    
    return JSON.parse(userJson);
  } catch (error) {
    console.error('Error parsing user:', error);
    return null;
  }
};

/**
 * Check if the current user has a specific permission
 * Works directly without using the PermissionContext
 * @param {string} permissionCode - The permission code to check
 * @returns {boolean} True if the user has the permission
 */
export const checkPermissionCode = (permissionCode) => {
  try {
    // Check if user has an 'admin' role by ID 1
    const userJson = localStorage.getItem('user');
    if (userJson) {
      const user = JSON.parse(userJson);
      
      // If user has roles and one of them is admin (ID 1), return true
      if (user && user.roles && Array.isArray(user.roles)) {
        if (user.roles.includes(1) || user.roles.some(role => role.id === 1)) {
          return true; // Admin has all permissions
        }
      }
    }
    
    // Get permissions from token if available
    const token = localStorage.getItem('token');
    if (token) {
      const parts = token.split('.');
      if (parts.length === 3) {
        const payload = JSON.parse(atob(parts[1]));
        if (payload && payload.permissions && Array.isArray(payload.permissions)) {
          return payload.permissions.includes(permissionCode);
        }
      }
    }
    
    return false;
  } catch (error) {
    console.error('Error checking permission:', error);
    return false;
  }
};

/**
 * Pause redirect during form submission
 * @param {boolean} isSubmitting - Whether a form is currently being submitted
 */
export const setFormSubmissionState = (isSubmitting) => {
  // Set global variable for cross-component communication
  window.__formSubmissionActive = isSubmitting;
  
  // Also dispatch event for components that listen for it
  window.dispatchEvent(new CustomEvent('formSubmissionState', {
    detail: { isSubmitting }
  }));
  
  console.log(`Form submission state set to: ${isSubmitting ? 'active' : 'inactive'}`);
};

/**
 * Get the JWT token from localStorage
 * @returns {string|null} JWT token or null if not found
 */
export const getAuthToken = () => {
  return localStorage.getItem('token');
};

/**
 * Get authorization header with JWT token
 * @returns {Object} Headers object with Authorization bearer token
 */
export const getAuthHeader = () => {
  const token = getAuthToken();
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

/**
 * Extract full permissions array from JWT
 * @returns {Array} Array of permission codes or empty array if none found
 */
export const getUserPermissions = () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return [];
    
    const parts = token.split('.');
    if (parts.length !== 3) return [];
    
    const payload = JSON.parse(atob(parts[1]));
    if (!payload || !payload.permissions) return [];
    
    return Array.isArray(payload.permissions) ? payload.permissions : [];
  } catch (error) {
    console.error('Error extracting permissions:', error);
    return [];
  }
};

export default {
  getCurrentUserId,
  getCurrentUser,
  checkPermissionCode,
  setFormSubmissionState,
  getAuthToken,
  getAuthHeader,
  getUserPermissions
};